import React, { useState, useEffect, useRef } from 'react';

interface DateInputProps {
    value: string;
    onChange: (dateObjStr: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    style?: React.CSSProperties;
    disabled?: boolean;
    defaultYear?: number;
    autoFocus?: boolean;
    /** 親から「日」欄にフォーカスを移すための ref（保存直後の再フォーカス等） */
    dayInputRef?: React.Ref<HTMLInputElement>;
}

const DateInput: React.FC<DateInputProps> = ({
    value, // "YYYY-MM-DD" or empty string
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    style,
    disabled = false,
    defaultYear = new Date().getFullYear(),
    autoFocus = false,
    dayInputRef,
}) => {
    // Parse initial value
    const parseValue = (val: string) => {
        if (!val || val.length !== 10) return { y: '', m: '', d: '' };
        const parts = val.split('-');
        return { y: parts[0], m: parts[1], d: parts[2] };
    };

    const initial = parseValue(value);
    const [year, setYear] = useState(initial.y);
    const [month, setMonth] = useState(initial.m);
    const [day, setDay] = useState(initial.d);

    const yearRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const dayRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const parsed = parseValue(value);
        setYear(parsed.y);
        setMonth(parsed.m);
        setDay(parsed.d);
    }, [value]);

    const handleCommit = (y: string, m: string, d: string) => {
        // If all are empty, clear it
        if (!y && !m && !d) {
             onChange('');
             return;
        }
        
        let finalY = parseInt(y, 10);
        let finalM = parseInt(m, 10);
        let finalD = parseInt(d, 10);

        if (isNaN(finalY)) finalY = defaultYear;
        if (isNaN(finalM)) finalM = new Date().getMonth() + 1;
        if (isNaN(finalD)) finalD = 1;

        if (finalM < 1) finalM = 1;
        if (finalM > 12) finalM = 12;
        if (finalD < 1) finalD = 1;
        if (finalD > 31) finalD = 31;

        const dateStr = `${finalY}-${String(finalM).padStart(2, '0')}-${String(finalD).padStart(2, '0')}`;
        onChange(dateStr);
    };

    const handleLocalBlur = () => {
        // Blurが発生したとき、フォーカスがyear, month, dayのいずれかであれば何もしない
        // 全てからフォーカスが外れた場合に確定処理を行う
        setTimeout(() => {
            if (
                document.activeElement !== yearRef.current &&
                document.activeElement !== monthRef.current &&
                document.activeElement !== dayRef.current
            ) {
                handleCommit(year, month, day);
                if (onBlur) onBlur();
            }
        }, 0);
    };

    const buildKeyDownHandler = (type: 'year' | 'month' | 'day') => (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCommit(year, month, day);
            if (onKeyDown) onKeyDown(e);
        } else if (e.key === 'Tab' && !e.shiftKey) {
            if (type === 'year') {
                e.preventDefault();
                monthRef.current?.focus();
            } else if (type === 'month') {
                e.preventDefault();
                dayRef.current?.focus();
            } else if (type === 'day') {
                if (onKeyDown) onKeyDown(e);
            }
        } else if (e.key === 'Tab' && e.shiftKey) {
            if (type === 'day') {
                e.preventDefault();
                monthRef.current?.focus();
            } else if (type === 'month') {
                e.preventDefault();
                yearRef.current?.focus();
            } else if (type === 'year') {
                if (onKeyDown) onKeyDown(e);
            }
        } else if (e.key === 'ArrowRight' && type === 'year' && yearRef.current?.selectionEnd === year.length) {
            monthRef.current?.focus();
        } else if (e.key === 'ArrowRight' && type === 'month' && monthRef.current?.selectionEnd === month.length) {
            dayRef.current?.focus();
        } else if (e.key === 'ArrowLeft' && type === 'day' && dayRef.current?.selectionStart === 0) {
            monthRef.current?.focus();
        } else if (e.key === 'ArrowLeft' && type === 'month' && monthRef.current?.selectionStart === 0) {
            yearRef.current?.focus();
        } else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const inputStyle: React.CSSProperties = {
        border: 'none',
        outline: 'none',
        textAlign: 'center',
        backgroundColor: disabled ? '#f0f0f0' : 'transparent',
        fontSize: '14px',
        fontFamily: 'MS UI Gothic, sans-serif',
        padding: '2px 0',
    };

    return (
        <div style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: disabled ? '#f0f0f0' : 'white',
            border: '2px inset #c0c0c0',
            boxSizing: 'border-box',
            padding: '2px 4px',
            color: disabled ? '#999' : 'black',
            cursor: disabled ? 'default' : 'text'
        }}
        onClick={() => {
            if (!disabled && document.activeElement !== yearRef.current && document.activeElement !== monthRef.current && document.activeElement !== dayRef.current) {
                dayRef.current?.focus();
            }
        }}>
            <input
                ref={yearRef}
                type="text"
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
                onBlur={handleLocalBlur}
                onKeyDown={buildKeyDownHandler('year')}
                onFocus={(e) => { e.target.select(); if (onFocus) onFocus(); }}
                disabled={disabled}
                style={{ ...inputStyle, width: '38px' }}
                placeholder="YYYY"
            />
            <span style={{ margin: '0 2px' }}>年</span>
            <input
                ref={monthRef}
                type="text"
                value={month}
                onChange={(e) => setMonth(e.target.value.replace(/[^\d]/g, '').slice(0, 2))}
                onBlur={handleLocalBlur}
                onKeyDown={buildKeyDownHandler('month')}
                onFocus={(e) => { e.target.select(); if (onFocus) onFocus(); }}
                disabled={disabled}
                style={{ ...inputStyle, width: '22px' }}
                placeholder="MM"
            />
            <span style={{ margin: '0 2px' }}>月</span>
            <input
                ref={(el) => {
                    (dayRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    if (typeof dayInputRef === 'function') {
                        dayInputRef(el);
                    } else if (dayInputRef != null) {
                        (dayInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    }
                }}
                type="text"
                value={day}
                onChange={(e) => setDay(e.target.value.replace(/[^\d]/g, '').slice(0, 2))}
                onBlur={handleLocalBlur}
                onKeyDown={buildKeyDownHandler('day')}
                onFocus={(e) => { e.target.select(); if (onFocus) onFocus(); }}
                disabled={disabled}
                autoFocus={autoFocus}
                style={{ ...inputStyle, width: '22px' }}
                placeholder="DD"
            />
            <span style={{ margin: '0 2px' }}>日</span>
        </div>
    );
};

export default DateInput;
